/** Drive a scene backend from the snapshot store: rebuild on a state change, not per frame. */

import type {
  CanvasArrangement,
  CueFrame,
  LayerPlacement,
  RegionMap,
  SceneDocument,
} from "@nebulize/sdk";

import type { CueStream } from "../transport/cues";
import { cueChannel } from "../transport/cues";
import type { EntityIndex, SnapshotState } from "../transport/snapshotStore";
import { useSnapshotStore } from "../transport/snapshotStore";
import { bindingsForCue } from "./cues";
import type { SceneBackend } from "./reconcile";
import { SceneMount } from "./reconcile";
import type { RenderedDocument } from "./sceneTree";
import { buildScene, regionsByName } from "./sceneTree";

export interface SceneSurface {
  /** Subscribe to canvas resizes and return the unsubscribe. */
  readonly onResize: (listener: () => void) => () => void;
  /** Hand each build's entities to whatever draws its own pixels. OVL-016. */
  readonly onEntities?: (entities: readonly unknown[], stale: boolean) => void;
}

/** The state one draw needs, whoever holds it: the overlay's store, or a host with props. */
export interface SceneState {
  readonly scenes: readonly SceneDocument[];
  readonly entities: EntityIndex;
  readonly regions: readonly RegionMap[];
  /**
   * Where the customer put each activity on each canvas of this space. OVL-027.
   *
   * The whole space's rather than this canvas kind's, because a host flipping between kinds holds
   * one state and would otherwise re-narrow it on every flip. `pushScene` and `attachSceneTo` pick
   * out the kind they draw. Absent leaves the compositor's own default rule standing.
   */
  readonly arrangement?: CanvasArrangement;
  /** Whether this state is still arriving. Handed to a surface drawing its own pixels. */
  readonly stale?: boolean;
}

/** The build option, present only when something is actually arranged.
 *
 * Spread rather than passed, because an explicit `undefined` and an absent key are not the same
 * thing under `exactOptionalPropertyTypes`, and `buildScene` reads absence as "leave the default
 * rule alone".
 */
function maybeArrangement(
  placements: Record<string, LayerPlacement> | undefined,
): { arrangement?: Record<string, LayerPlacement> } {
  return placements === undefined ? {} : { arrangement: placements };
}

/** One canvas kind's placements out of the whole space's arrangement. */
export function placementsOn(
  arrangement: CanvasArrangement | undefined,
  canvasKind: string,
): Record<string, LayerPlacement> | undefined {
  return arrangement?.canvases?.[canvasKind];
}

/** What an attached scene draws, and where its cues come from.
 *
 * `canvasKind` has no default on purpose. This host draws exactly the kind its address named, and a
 * default here is what made every canvas but one draw nothing at all: the page read `?canvas=` and
 * the build was told `broadcast` regardless, so every other document hit the drop-and-report path.
 */
export interface SceneAttachment {
  /** Which canvas kind this page draws, off its own address. */
  readonly canvasKind: string;
  /** The cues to play. Defaults to the one channel the host connection feeds. */
  readonly cues?: CueStream;
}

/** A backend a host pushes state into, rather than one that reads a store of its own. */
export interface PushedScene {
  /** Draw this state. Call it whenever the state changes; it reconciles rather than rebuilds. */
  apply: (state: SceneState) => void;
  /** Re-place what is already drawn, which is what a resize needs. */
  redraw: () => void;
  /** Play one cue against whatever the drawn documents bound its tag to. */
  present: (frame: CueFrame) => void;
  clear: () => void;
}

/** Mount a scene onto a backend for a host that holds its own state and its own canvas kind.
 *
 * `canvasKind` is required for the reason `SceneAttachment` says: a default draws one kind forever.
 */
export function pushScene<Handle>(
  surface: SceneSurface,
  backend: SceneBackend<Handle>,
  canvasKind: string,
): PushedScene {
  const mount = new SceneMount(backend);
  let drawn: readonly RenderedDocument[] = [];

  return {
    apply(state) {
      drawn = buildScene(state.scenes, state.entities, {
        canvasKind,
        regions: regionsByName(state.regions),
        ...maybeArrangement(placementsOn(state.arrangement, canvasKind)),
      });
      mount.apply(drawn);
      surface.onEntities?.([...state.entities.values()], state.stale === true);
    },
    redraw() {
      mount.apply(drawn);
    },
    present(frame) {
      if (backend.present === undefined) return;
      for (const binding of bindingsForCue(drawn, frame.cue.tag)) {
        const handle = mount.find(binding.nodeKey);
        if (handle === undefined) continue;
        backend.present(handle, binding.presentation, frame.cue);
      }
    },
    clear() {
      mount.clear();
    },
  };
}

/** Draw the held state into `backend` until the returned detach is called. */
export function attachSceneTo<Handle>(
  surface: SceneSurface,
  backend: SceneBackend<Handle>,
  attachment: SceneAttachment,
): () => void {
  const canvasKind = attachment.canvasKind;
  const cues = attachment.cues ?? cueChannel;
  const mount = new SceneMount(backend);
  let drawn: readonly RenderedDocument[] = [];

  function draw(state: SnapshotState): void {
    drawn = buildScene(state.scenes, state.entities, {
      canvasKind,
      regions: regionsByName(state.regions),
      ...maybeArrangement(placementsOn(state.arrangement, canvasKind)),
    });
    mount.apply(drawn);
    hand(state);
  }

  /** Hand this state to whatever draws its own pixels, saying whether it is still arriving. */
  function hand(state: SnapshotState): void {
    surface.onEntities?.([...state.entities.values()], state.health !== "live");
  }

  /** Hand one cue to every live node a drawn document bound its tag to. */
  function present(frame: CueFrame): void {
    if (backend.present === undefined) return;

    for (const binding of bindingsForCue(drawn, frame.cue.tag)) {
      const handle = mount.find(binding.nodeKey);
      if (handle === undefined) continue;
      backend.present(handle, binding.presentation, frame.cue);
    }
  }

  draw(useSnapshotStore.getState());

  const unsubscribe = useSnapshotStore.subscribe((state, previous) => {
    if (
      state.scenes === previous.scenes &&
      state.entities === previous.entities &&
      state.regions === previous.regions
    ) {
      // The transport dropped or came back with the same state held. Nothing to rebuild, but a
      // surface drawing its own pixels is owed the news, because only it can decide what to do.
      if (state.health !== previous.health) hand(state);
      return;
    }
    draw(state);
  });

  const stopResizing = surface.onResize(() => {
    mount.apply(drawn);
  });

  const stopCues = cues.subscribe(present);

  return () => {
    stopCues();
    stopResizing();
    unsubscribe();
    mount.clear();
  };
}
